import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Box,
  CircularProgress,
  Snackbar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { AdminSettingsModel, IAdminSettings } from "../../models/AdminSettingsModel";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";

const AdminSettingsView: React.FC = () => {
  const [servicesList, setServicesList] = useState<{ name: string; default: boolean; qboItemName?: string }[]>([]);

  const [settings, setSettings] = useState<IAdminSettings>({
    sendConfirmationEnabled: true,
    confirmationSendDaysBeforeDelivery: 2,
    services: [{ name: "4000 Gallons", default: true }],
    truckNumbers: ["123456789"],
    driverInitials: ["A"],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editServiceIndex, setEditServiceIndex] = useState<number | null>(null);
  const [newServiceName, setNewServiceName] = useState("");
  const [editingServiceName, setEditingServiceName] = useState("");
  const [truckNumbers, setTruckNumbers] = useState<string[]>([]);
  const [newTruckNumber, setNewTruckNumber] = useState("");
  const [editTruckIndex, setEditTruckIndex] = useState<number | null>(null);
  const [editingTruckNumber, setEditingTruckNumber] = useState("");
  const [driverInitials, setDriverInitials] = useState<string[]>([]);
  const [editDriverInitialIndex, setEditDriverInitialIndex] = useState<number | null>(null);
  const [newDriverInitial, setNewDriverInitial] = useState("");
  const [editingDriverInitial, setEditingDriverInitial] = useState("");
  const [editingServiceDefault, setEditingServiceDefault] = useState(false);
  const [editingServiceQboItemName, setEditingServiceQboItemName] = useState("");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await AdminSettingsModel.getSettings();
        setSettings(data);
        // Set servicesList properly
        if (Array.isArray(data.services)) {
          setServicesList(data.services.map(service =>
            typeof service === 'string' ? { name: service, default: false } : service
          ));
        } else {
          setServicesList([]);
        }
      } catch {
        setError("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchTruckNumbers = async () => {
      try {
        const data = await AdminSettingsModel.getTruckNumbers();
        setTruckNumbers(data);
      } catch {
        setError("Failed to load truck numbers");
      } finally {
        setLoading(false);
      }
    };
    fetchTruckNumbers();
  }, []);

  useEffect(() => {
    const fetchDriverInitials = async () => {
      try {
        const data = await AdminSettingsModel.getDriverInitials();
        setDriverInitials(data);
      } catch {
        setError("Failed to load driver initials");
      } finally {
        setLoading(false);
      }
    };
    fetchDriverInitials();
  }, []);

  const handleToggleDefaultService = (index: number) => {
    const isCurrentlyDefault = servicesList[index].default;
    const newServices = servicesList.map((service, i) => ({
      ...service,
      default: i === index ? !isCurrentlyDefault : false,
    }));
    setServicesList(newServices);
    setSettings(prev => ({ ...prev, services: newServices }));
  };

  const handleToggle = () => {
    setSettings((prev) => ({
      ...prev,
      sendConfirmationEnabled: !prev.sendConfirmationEnabled,
    }));
  };

  const handleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 0) {
      setSettings((prev) => ({
        ...prev,
        confirmationSendDaysBeforeDelivery: value,
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await AdminSettingsModel.updateSettings(settings);
      setSuccess(true);
    } catch {
      setError("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleAddService = () => {
    if (newServiceName.trim() !== "") {
      const newServices = [...servicesList, { name: newServiceName.trim(), default: false }];
      setServicesList(newServices);
      setSettings((prev) => ({ ...prev, services: newServices }));
      setNewServiceName("");
    }
  };

  const handleAddTruckNumber = () => {
    if (newTruckNumber.trim() !== "") {
      const newTruckNumbers = [...truckNumbers, newTruckNumber.trim()];
      setTruckNumbers(newTruckNumbers);
      setSettings((prev) => ({ ...prev, truckNumbers: newTruckNumbers }));
      setNewTruckNumber("");
    }
  };

  const handleAddDriverInitial = () => {
    if (newDriverInitial.trim() !== "") {
      const newDriverInitials = [...driverInitials, newDriverInitial.trim()];
      setDriverInitials(newDriverInitials);
      setSettings((prev) => ({ ...prev, driverInitials: newDriverInitials }));
      setNewDriverInitial("");
    }
  };

  const handleDeleteService = (index: number) => {
    const newServices = servicesList.filter((_, i) => i !== index);
    setServicesList(newServices);
    setSettings((prev) => ({ ...prev, services: newServices }));
  };

  const handleDeleteTruckNumber = (index: number) => {
    const newTruckNumbers = truckNumbers.filter((_, i) => i !== index);
    setTruckNumbers(newTruckNumbers);
    setSettings((prev) => ({ ...prev, truckNumbers: newTruckNumbers }));
  };

  const handleDeleteDriverInitial = (index: number) => {
    const newDriverInitials = driverInitials.filter((_, i) => i !== index);
    setDriverInitials(newDriverInitials);
    setSettings((prev) => ({ ...prev, driverInitials: newDriverInitials }));
  };

  const handleOpenEditDialog = (index: number) => {
    setEditServiceIndex(index);
    setEditingServiceName(servicesList[index].name);
    setEditingServiceDefault(servicesList[index].default);
    setEditingServiceQboItemName(servicesList[index].qboItemName || "");
    setOpenDialog(true);
  };

  const handleOpenEditTruckNumberDialog = (index: number) => {
    setEditTruckIndex(index);
    setEditingTruckNumber(truckNumbers[index]);
    setOpenDialog(true);
  };

  const handleOpenEditDriverInitialDialog = (index: number) => {
    setEditDriverInitialIndex(index);
    setEditingDriverInitial(driverInitials[index]);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditServiceIndex(null);
    setEditingServiceName("");
    setEditingServiceQboItemName("");
  };

  const handleSaveEdit = () => {
    if (editServiceIndex !== null && editingServiceName.trim() !== "") {
      const newServices = [...servicesList];
      newServices[editServiceIndex] = {
        name: editingServiceName.trim(),
        default: editingServiceDefault,
        qboItemName: editingServiceQboItemName.trim(),
      };
      setServicesList(newServices);
      setSettings(prev => ({ ...prev, services: newServices }));
      handleCloseDialog();
    }

    if (editTruckIndex !== null && editingTruckNumber.trim() !== "") {
      const newTruckNumbers = [...truckNumbers];
      newTruckNumbers[editTruckIndex] = editingTruckNumber.trim();
      setTruckNumbers(newTruckNumbers);
      setSettings((prev) => ({ ...prev, truckNumbers: newTruckNumbers }));
      handleCloseDialog();
    }

    if (editDriverInitialIndex !== null && editingDriverInitial.trim() !== "") {
      const newDriverInitials = [...driverInitials];
      newDriverInitials[editDriverInitialIndex] = editingDriverInitial.trim();
      setDriverInitials(newDriverInitials);
      setSettings((prev) => ({ ...prev, driverInitials: newDriverInitials }));
      handleCloseDialog();
    }
  };

  const handleSaveServices = async () => {
    try {
      await AdminSettingsModel.updateServices({ services: servicesList });
      setSuccess(true);
    } catch {
      setError("Failed to save services");
    }
  };

  const handleSaveTruckNumbers = async () => {
    try {
      await AdminSettingsModel.updateTruckNumbers({ truckNumbers: truckNumbers });
      setSuccess(true);
    } catch {
      setError("Failed to save truck numbers");
    }
  };

  const handleSaveDriverInitials = async () => {
    try {
      await AdminSettingsModel.updateDriverInitials({ driverInitials: driverInitials });
      setSuccess(true);
    } catch {
      setError("Failed to save driver initials");
    }
  };

  if (loading) {
    return (
      <Container style={{ minHeight: "75vh", paddingTop: "3rem" }}>
        <Typography variant="h5" align="center">
          Loading settings...
        </Typography>
      </Container>
    );
  }

  return (
    <Container style={{ minHeight: "75vh", paddingTop: "3rem" }} maxWidth="sm">
      <Typography variant="h4" style={{ marginBottom: "1.5rem", fontWeight: "bold" }}>
        Admin Settings
      </Typography>

      {error && (
        <Box mb={2}>
          <Typography color="error">
            {error}
          </Typography>
        </Box>
      )}

      {/* ------------------------------ */}

      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        mb={3}
        p={2}
        style={{
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          width: "100%"
        }}
      >
        <Typography variant="h5" style={{ marginBottom: "1rem", fontWeight: "bold" }}>
          Order Confirmation Settings
        </Typography>

        <Box display="flex" alignItems="center" mb={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.sendConfirmationEnabled}
                onChange={handleToggle}
                color="primary"
              />
            }
            label="Enable sending delivery confirmation"
            style={{ marginRight: "2rem" }}
          />
        </Box>

        <Box display="flex" alignItems="center" width="100%">
          <TextField
            label="Days before delivery to send confirmation"
            type="number"
            inputProps={{ min: 0 }}
            value={settings.confirmationSendDaysBeforeDelivery}
            onChange={handleDaysChange}
            disabled={!settings.sendConfirmationEnabled}
            variant="outlined"
            size="small"
            fullWidth
          />
        </Box>

        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            fullWidth
          >
            {saving ? <CircularProgress size={24} /> : "Save Settings"}
          </Button>
        </Box>
      </Box>

      {/* ------------------------------ */}

      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        mb={3}
        p={2}
        style={{
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          width: "100%"
        }}
      >
        <Typography variant="h5" style={{ marginBottom: "1.5rem", fontWeight: "bold" }}>
          Services List
        </Typography>

        <Box display="flex" flexDirection="column" width="100%" mb={2}>
          <Box display="flex" alignItems="center" mb={1}>
            <TextField
              value={newServiceName}
              onChange={(e) => setNewServiceName(e.target.value)}
              placeholder="New service name"
              variant="outlined"
              size="small"
              fullWidth
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddService}
              style={{
                height: "100%",
                width: "100%",
                marginLeft: "10px",
                minWidth: "120px"
              }}
            >
              Add Service
            </Button>
          </Box>
        </Box>

        <Box width="100%">
          <List>
            {servicesList.map((service, index) => (
              <ListItem key={index} style={{ padding: "8px 0", display: "flex", alignItems: "center" }}>
                <ListItemText primary={service.name} style={{ flex: 1 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={service.default}
                      onChange={() => handleToggleDefaultService(index)}
                      color="primary"
                    />
                  }
                  label="Default"
                  style={{ marginRight: "96px" }}
                />
                <ListItemSecondaryAction>
                  <MuiIconButton
                    edge="end"
                    size="small"
                    aria-label="edit"
                    onClick={() => handleOpenEditDialog(index)}
                    style={{ color: "#1976d2" }}
                  >
                    <EditIcon />
                  </MuiIconButton>
                  <MuiIconButton
                    edge="end"
                    size="small"
                    aria-label="delete"
                    onClick={() => handleDeleteService(index)}
                    style={{ marginLeft: "8px", color: "#f44336" }}
                  >
                    <DeleteIcon />
                  </MuiIconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>

        <Box mt={2} width="100%">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveServices}
            fullWidth
          >
            Save Services
          </Button>
        </Box>
      </Box>

      {/* ------------------------------ */}

      {/*
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        mb={3}
        p={2}
        style={{
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          width: "100%"
        }}
      >
        <Typography variant="h5" style={{ marginBottom: "1.5rem", fontWeight: "bold" }}>
          Truck Numbers List
        </Typography>

        <Box display="flex" flexDirection="column" width="100%" mb={2}>
          <Box display="flex" alignItems="center" mb={1}>
            <TextField
              value={newTruckNumber}
              onChange={(e) => setNewTruckNumber(e.target.value)}
              placeholder="Add truck number"
              variant="outlined"
              size="small"
              fullWidth
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddTruckNumber}
              style={{
                height: "100%",
                width: "100%",
                marginLeft: "10px",
                minWidth: "120px"
              }}
            >
              Add Truck Number
            </Button>
          </Box>
        </Box>

        <Box width="100%">
          <List>
            {truckNumbers.map((truckNumber, index) => (
              <ListItem key={index} style={{ padding: "8px 0" }}>
                <ListItemText
                  primary={truckNumber}
                  style={{ flex: 1 }}
                />
                <ListItemSecondaryAction>
                  <MuiIconButton
                    edge="end"
                    size="small"
                    aria-label="edit"
                    onClick={() => handleOpenEditTruckNumberDialog(index)}
                    style={{ color: "#1976d2" }}
                  >
                    <EditIcon />
                  </MuiIconButton>
                  <MuiIconButton
                    edge="end"
                    size="small"
                    aria-label="delete"
                    onClick={() => handleDeleteTruckNumber(index)}
                    style={{ marginLeft: "8px", color: "#f44336" }}
                  >
                    <DeleteIcon />
                  </MuiIconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>

        <Box mt={2} width="100%">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveTruckNumbers}
            fullWidth
          >
            Save Truck Numbers
          </Button>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        mb={3}
        p={2}
        style={{
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          width: "100%"
        }}
      >
        <Typography variant="h5" style={{ marginBottom: "1.5rem", fontWeight: "bold" }}>
          Driver Initials List
        </Typography>

        <Box display="flex" flexDirection="column" width="100%" mb={2}>
          <Box display="flex" alignItems="center" mb={1}>
            <TextField
              value={newDriverInitial}
              onChange={(e) => setNewDriverInitial(e.target.value)}
              placeholder="Add driver initial"
              variant="outlined"
              size="small"
              fullWidth
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddDriverInitial}
              style={{
                height: "100%",
                width: "100%",
                marginLeft: "10px",
                minWidth: "120px"
              }}
            >
              Add Driver Initial
            </Button>
          </Box>
        </Box>

        <Box width="100%">
          <List>
            {driverInitials.map((initial, index) => (
              <ListItem key={index} style={{ padding: "8px 0" }}>
                <ListItemText
                  primary={initial}
                  style={{ flex: 1 }}
                />
                <ListItemSecondaryAction>
                  <MuiIconButton
                    edge="end"
                    size="small"
                    aria-label="edit"
                    onClick={() => handleOpenEditDriverInitialDialog(index)}
                    style={{ color: "#1976d2" }}
                  >
                    <EditIcon />
                  </MuiIconButton>
                  <MuiIconButton
                    edge="end"
                    size="small"
                    aria-label="delete"
                    onClick={() => handleDeleteDriverInitial(index)}
                    style={{ marginLeft: "8px", color: "#f44336" }}
                  >
                    <DeleteIcon />
                  </MuiIconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>

        <Box mt={2} width="100%">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveDriverInitials}
            fullWidth
          >
            Save Driver Initials
          </Button>
        </Box>
      </Box>
      */}


      <Dialog open={openDialog} onClose={handleCloseDialog}>
        {editServiceIndex !== null && (
          <>
            <DialogTitle>Edit Service</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Service Name"
                type="text"
                fullWidth
                value={editingServiceName}
                onChange={(e) => setEditingServiceName(e.target.value)}
              />
              <TextField
                margin="dense"
                label="QuickBooks Item Name"
                type="text"
                fullWidth
                value={editingServiceQboItemName}
                onChange={(e) => setEditingServiceQboItemName(e.target.value)}
                helperText="Exact QuickBooks product/service name (e.g. Hauled Potable Water:4,000 Gallons - Approximate). Leave blank to use the service name."
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={handleSaveEdit} color="primary">
                Save
              </Button>
            </DialogActions>
          </>
        )}
        {editTruckIndex !== null && (
          <>
            <DialogTitle>Edit Truck Number</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Truck Number"
                type="text"
                fullWidth
                value={editingTruckNumber}
                onChange={(e) => setEditingTruckNumber(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={handleSaveEdit} color="primary">
                Save
              </Button>
            </DialogActions>
          </>
        )}
        {editDriverInitialIndex !== null && (
          <>
            <DialogTitle>Edit Driver Initial</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Driver Initial"
                type="text"
                fullWidth
                value={editingDriverInitial}
                onChange={(e) => setEditingDriverInitial(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button onClick={handleSaveEdit} color="primary">
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        message="Settings saved successfully!"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setSuccess(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Container>
  );
};

export default AdminSettingsView;

